import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Comment from "./Comment";
import { IoMdArrowBack, IoMdClose, IoIosArrowDown } from "react-icons/io";
import { Button } from "@material-tailwind/react";
import Skeleton from "react-loading-skeleton";
import toast, { Toaster } from "react-hot-toast";
import { neighborhoodNewsData } from "../../assets/data/Datas";
import BeatLoader from "react-spinners/BeatLoader";
import { BsBookmarkCheckFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import { CiBookmarkPlus, CiSearch } from "react-icons/ci";
import { IoLocationOutline } from "react-icons/io5";
import {
    FaHeart,
    FaRegComment,
    FaRegHeart,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { postNeighborhoodSavedNews } from "../../redux/ApiSlice/Neighborhood/NeighBorhoodNewsSavedSlice";
import {
    reportNewsComment,
    userReportNews,
} from "../../redux/ApiSlice/Neighborhood/NeighborhoodNewsReportSlice";
import {
    deleteNeighborhoodNews,
    getSingleNeighborhoodNews,
    likeNeighborhoodNews,
} from "../../redux/ApiSlice/Neighborhood/NeighborhoodNewsSlice";
import {
    createNeighborhoodNewsComment,
    deleteNeighborhoodNewsComment,
    getNeighborhoodNewsComment,
    likeDislikeNeighborhoodNewsComment,
    updateNeighborhoodNewsComment,
} from "../../redux/ApiSlice/Neighborhood/NeighBorhoodNewsCommentSlice";
import { getAndCreateChatRoom } from "../../redux/ApiSlice/MessagesSlice";
import { accountBlockUser } from "../../redux/ApiSlice/AccountProfileSlice";
import { handleProfileNavigate } from "../../utils/handleProfileNavigate";
import { SendHorizontal } from 'lucide-react';

// Moved outside component — stable references, no recreation on each render
const reportReasons = [
    "Harassment",
    "Inappropriate",
    "Bullying or targeted harassment",
    "Hate speech",
    "Blackmail",
    "Sexual Harassment",
    "Impersonation/Fake account",
    "Graphic Violence/Gore",
    "Spam",
    "Drugs/Weapons",
    "Sexual content involving suspected minors",
    "Stalking or Intimidation",
    "Child safety concerns",
    "Self-harm or suicide content",
    "Violence or physical harm threats",
    "Dangerous acts & challenges",
    "Human exploitation/trafficking",
    "Scams/Fraud/Phishing",
    "Illegal Services/Solicitation",
    "IP Infringement",
    "Other"
];

function formatTimeOnly(dateString) {
    const date = new Date(dateString);
    const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    let timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    timeString = timeString.replace(/am|pm/i, match => match.toUpperCase());
    return `${formattedDate} at ${timeString}`;
}

const NewsDetails = () => {
    const { id } = useParams();
    const [newsDetails, setNewsDetails] = useState(null);

    const dispatch = useDispatch();
    const NeighborhoodNews = useSelector((state) => state.NeighborhoodNews);
    const neighborhoodNewsComment = useSelector(
        (state) => state.NeighborhoodNewsComment
    );
    const reportNews = useSelector((state) => state.NeighborhoodReportNews);
    const { User_profile } = useSelector((state) => state.AccountProfile);

    const menuRef = useRef(null);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const navigate = useNavigate();

    const [sharePopupOpen, setSharePopupOpen] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const sharePopupRef = useRef(null);

    const [commentPopupOpen, setCommentPopupOpen] = useState(false);
    const commentPopupRef = useRef(null);
    // Ref keeps handleClickOutside's closure fresh without re-registering the listener
    const commentPopupOpenRef = useRef(false);

    const [newsComment, setNewsComment] = useState("");

    const [comments, setComments] = useState([]);
    const [selectedCommentId, setSelectedCommentId] = useState(null);
    const [reportCommentText, setReportCommentText] = useState("");
    const [commentsNextUrl, setCommentsNextUrl] = useState(null);
    const [loadingMoreComments, setLoadingMoreComments] = useState(false);

    const [commentResport, setCommentResport] = useState(false);
    const [isReportNews, setIsReportNews] = useState(false);
    const [newsReportText, setNewsReportText] = useState("");
    const [page, setPage] = useState(1);

    const [reportReason, setReportReason] = useState("");
    const [commentReportReason, setCommentReportReason] = useState("");
    const [showReasonDropdown, setShowReasonDropdown] = useState(false);
    const [showCommentReasonDropdown, setShowCommentReasonDropdown] = useState(false);
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);
    const [blockUserInfo, setBlockUserInfo] = useState(null);

    const [expandedNews, setExpandedNews] = useState({});

    const theme = localStorage.getItem("theme") || "light";

    // Define colors for light and dark themes
    const skeletonColors = theme === "dark"
        ? { baseColor: "#23272F", highlightColor: "#3A3F4B" }
        : { baseColor: "#CECECE", highlightColor: "#E8E8E8" };

    // Keep ref in sync so the stable handleClickOutside closure reads current value
    useEffect(() => {
        commentPopupOpenRef.current = commentPopupOpen;
    }, [commentPopupOpen]);

    // Memoized so handleProfileNavigate isn't called on every render
    const handleNavigate = useMemo(
        () => handleProfileNavigate(navigate, User_profile?.id),
        [navigate, User_profile?.id]
    );

    const handleToggleExpand = (newsId) => {
        setExpandedNews((prev) => ({
            ...prev,
            [newsId]: !prev[newsId],
        }));
    };

    const handleReasonSelect = (selectedReason) => {
        setReportReason(selectedReason);
        setShowReasonDropdown(false);
    };

    const handleCommentReasonSelect = (selectedReason) => {
        setCommentReportReason(selectedReason);
        setShowCommentReasonDropdown(false);
    };

    const handleBlockUser = () => {
        setBlockUserInfo({
            full_name: newsDetails?.created_by?.full_name,
        });
        setShowBlockConfirm(true);
        setActiveMenuId(null);
    };

    const handleBlockConfirm = async () => {
        try {
            const result = await dispatch(accountBlockUser(newsDetails?.created_by?.id));
            if (result.error) {
                toast.error("Failed to block user. Please try again.");
                console.error("Error blocking user:", result.error);
                return;
            } else {
                toast.success("User blocked successfully.");
            }
            setShowBlockConfirm(false);
            setBlockUserInfo(null);
        } catch (error) {
            console.error("Error blocking user:", error);
            setShowBlockConfirm(false);
            setBlockUserInfo(null);
        }
    };

    const handleBlockCancel = () => {
        setShowBlockConfirm(false);
        setBlockUserInfo(null);
    };

    // Removed loadingMoreComments from deps — the guard inside reads it at call time
    const loadMoreComments = useCallback(async () => {
        if (!commentsNextUrl || loadingMoreComments) return;

        setLoadingMoreComments(true);
        try {
            const nextPage = page + 1;
            const response = await dispatch(
                getNeighborhoodNewsComment({ id, page: nextPage })
            ).unwrap();

            if (response?.results) {
                setComments(prevComments => [...prevComments, ...response.results]);
                setCommentsNextUrl(response.next || null);
                setPage(nextPage);
            }
        } catch (error) {
            console.error("Failed to load more comments:", error);
            toast.error("Failed to load more comments");
        } finally {
            setLoadingMoreComments(false);
        }
    }, [commentsNextUrl, id, dispatch, page]);

    const handleCommentsScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            if (commentsNextUrl && !loadingMoreComments) {
                loadMoreComments();
            }
        }
    }, [commentsNextUrl, loadingMoreComments, loadMoreComments]);

    const initialComments = useCallback(async () => {
        try {
            const response = await dispatch(getNeighborhoodNewsComment({ id, page: 1 })).unwrap();
            if (response?.results) {
                setComments(response.results);
                setCommentsNextUrl(response.next || null);
                setPage(1);
            }
        } catch (error) {
            console.error("Error fetching initial comments:", error);
        }
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(getSingleNeighborhoodNews({ id }));
        initialComments();
    }, [id, dispatch, initialComments]);

    useEffect(() => {
        if (NeighborhoodNews.neighborhoodNews) {
            setNewsDetails(NeighborhoodNews.neighborhoodNews);
        }
    }, [NeighborhoodNews.neighborhoodNews]);

    // Removed the redundant neighborhoodNewsComments useEffect — it caused duplicate
    // comment appends because loadMoreComments already sets state via unwrap() directly.

    const handleMenuToggle = (menuId, event) => {
        event.stopPropagation();
        setActiveMenuId(activeMenuId === menuId ? null : menuId);
    };

    // Stable callback — reads commentPopupOpen via ref to avoid stale closure
    const handleClickOutside = useCallback((event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setActiveMenuId(null);
        }
        if (sharePopupRef.current && !sharePopupRef.current.contains(event.target)) {
            setSharePopupOpen(false);
        }
        if (commentPopupOpenRef.current && event.target.classList.contains('comment-modal-overlay')) {
            setCommentPopupOpen(false);
            setSelectedCommentId(null);
            setNewsComment("");
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setShowReasonDropdown(false);
                setShowCommentReasonDropdown(false);
            }
        };

        if (showReasonDropdown || showCommentReasonDropdown) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showReasonDropdown, showCommentReasonDropdown]);

    // Added navigate to deps — was missing, causing stale closure
    const handleDeleteNeighborhoodNews = useCallback(
        async (newsId) => {
            try {
                await dispatch(deleteNeighborhoodNews({ id: newsId })).unwrap();
                toast.success("Neighborhood news deleted successfully.");
                setTimeout(() => {
                    navigate("/neighborhood");
                }, 3000);
            } catch (error) {
                toast.error("Failed to delete neighborhood news. Please try again later.");
            }
        },
        [dispatch, navigate]
    );

    const handleLikeNeighborhoodNews = async (newsId) => {
        try {
            const response = await dispatch(likeNeighborhoodNews({ id: newsId })).unwrap();
            if (response.message === "News liked successfully.") {
                setNewsDetails((prevDetails) => ({
                    ...prevDetails,
                    likes_count: prevDetails.likes_count + 1,
                    is_liked: true,
                }));
            } else if (response.message === "News unliked successfully.") {
                setNewsDetails((prevDetails) => ({
                    ...prevDetails,
                    likes_count: prevDetails.likes_count - 1,
                    is_liked: false,
                }));
            }
        } catch (error) {
            console.error("Failed to like neighborhood news:", error);
        }
    };

    const handleAddCommentNews = async () => {
        const data = { body: newsComment };
        if (!newsComment) {
            return toast.error("Please enter a comment.");
        }
        try {
            const response = await dispatch(
                createNeighborhoodNewsComment({ id, data })
            ).unwrap();
            if (response.body) {
                setNewsComment("");
                setCommentPopupOpen(false);
                dispatch(getSingleNeighborhoodNews({ id }));
                // Fixed: was incrementing `comments` (wrong field), now uses `comments_count`
                setNewsDetails((prevDetails) => ({
                    ...prevDetails,
                    comments_count: prevDetails.comments_count + 1,
                }));
                const commentsResponse = await dispatch(getNeighborhoodNewsComment({
                    id,
                    page: 1
                })).unwrap();
                if (commentsResponse?.results) {
                    setComments(commentsResponse.results);
                    setCommentsNextUrl(commentsResponse.next || null);
                    setPage(1);
                }
            }
        } catch (error) {
            console.error("Failed to add comment:", error);
        }
    };

    const handleDeleteNewsComment = async (commentId) => {
        try {
            await dispatch(deleteNeighborhoodNewsComment({ commentId })).unwrap();
            setNewsDetails((prevDetails) => ({
                ...prevDetails,
                comments_count: prevDetails.comments_count - 1,
            }));
            setComments((prevComments) =>
                prevComments.filter((comment) => comment?.id !== commentId)
            );
            if (comments.length < 10 && commentsNextUrl) {
                loadMoreComments();
            }
            toast.success("Comment deleted successfully.");
        } catch (error) {
            console.error("Failed to delete comment:", error);
        }
    };

    const submitEditNewsComment = async (commentId) => {
        const data = { body: newsComment };
        try {
            const response = await dispatch(
                updateNeighborhoodNewsComment({ commentId, data })
            ).unwrap();
            if (response) {
                setComments((prevComments) =>
                    prevComments.map((comment) =>
                        comment.id === commentId ? response : comment
                    )
                );
                setNewsComment("");
                setCommentPopupOpen(false);
                setSelectedCommentId(null);
                toast.success("Comment updated successfully.");
            }
        } catch (error) {
            console.error("Failed to update comment:", error);
        }
    };

    const handleLikeNeighborhoodNewsComment = async (commentId) => {
        try {
            const response = await dispatch(
                likeDislikeNeighborhoodNewsComment({ commentId })
            ).unwrap();
            if (response.message === "Like added to the comment") {
                setComments((prevComments) =>
                    prevComments.map((comment) =>
                        comment.id === commentId
                            ? { ...comment, likes_count: comment.likes_count + 1, is_liked: true }
                            : comment
                    )
                );
            } else if (response.message === "Like removed form the comment") {
                setComments((prevComments) =>
                    prevComments.map((comment) =>
                        comment.id === commentId
                            ? { ...comment, likes_count: comment.likes_count - 1, is_liked: false }
                            : comment
                    )
                );
            }
        } catch (error) {
            console.error("Failed to like neighborhood news comment:", error);
        }
    };

    const handleNewsSave = async (newsId) => {
        try {
            const response = await dispatch(postNeighborhoodSavedNews(newsId)).unwrap();
            if (response.detail === "News successfully saved.") {
                setNewsDetails((prevDetails) => ({ ...prevDetails, is_saved: true }));
                toast.success("News saved successfully.");
            } else {
                setNewsDetails((prevDetails) => ({ ...prevDetails, is_saved: false }));
                toast.error("News unsaved successfully.");
            }
        } catch (error) {
            console.error("Failed to save neighborhood news:", error);
        }
    };

    const handleEditNewsComment = (editId) => {
        setCommentPopupOpen(true);
        setSelectedCommentId((prevId) => (prevId === editId ? null : editId));
        const commentToEdit = comments.find((comment) => comment.id === editId);
        if (commentToEdit) {
            setNewsComment(commentToEdit.body);
        }
    };

    const submitReportComment = async () => {
        const data = {
            reason: commentReportReason,
            message: reportCommentText,
        };
        if (!commentReportReason) {
            return toast.error("Please select a reason.");
        }
        try {
            const response = await dispatch(
                reportNewsComment({ commentId: selectedCommentId, data })
            ).unwrap();
            if (response) {
                setCommentResport(false);
                toast.success(response.message);
                setReportCommentText("");
                setCommentReportReason("");
                const reportedCommentId = selectedCommentId;
                setSelectedCommentId(null);
                setComments((prevComments) =>
                    prevComments.filter((comment) => comment.id !== reportedCommentId)
                );
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const submitReportNews = async () => {
        const data = {
            reason: reportReason,
            message: newsReportText,
        };
        if (!reportReason) {
            return toast.error("Please select a reason.");
        }
        try {
            const response = await dispatch(
                userReportNews({ newsId: newsDetails.id, data })
            ).unwrap();
            if (response) {
                setIsReportNews(false);
                toast.success(response.message);
                setNewsReportText("");
                setReportReason("");
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleStartChat = async (userId) => {
        try {
            const response = await dispatch(getAndCreateChatRoom(userId));
            if (response.payload.id) {
                navigate(`/message/${response.payload.id}`);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleReportComment = (commentId) => {
        setSelectedCommentId(commentId);
        setCommentResport(true);
    };

    // Memoized — only recomputes when searchInput changes, not on every render
    const filteredResults = useMemo(() =>
        neighborhoodNewsData.filter((data) =>
            data.firstName.toLowerCase().includes(searchInput.toLowerCase())
        ),
        [searchInput]
    );

    // Fixed: was `!newsDetails` which never fired since initial state was `{}` (truthy).
    // Now correctly waits for loading to finish before declaring not found.
    if (!newsDetails?.id && !NeighborhoodNews.loading) {
        return <div>News not found</div>;
    }

    return (
        <Fragment>
            <div className="sticky -top-[15rem] bg-white dark:bg-[#1E1D21] dark:text-white pb-[2px] z-10">
                <div className="h-12 flex items-center justify-center gap-x-4 bg-[#BDE6FF] shadow-md rounded-b-xl relative">
                    <IoMdArrowBack
                        onClick={() => navigate("/neighborhood")}
                        className="absolute left-2 text-[24px] cursor-pointer text-black"
                    />
                    <p className="text-[16px] font-bold text-black dark:text-black line-clamp-1 truncate">
                        News Details
                    </p>
                </div>

                {NeighborhoodNews.loading && (
                    <div className="grid grid-cols-12 p-2 py-2 mt-1">
                        <div className="col-span-2 flex justify-start items-center">
                            <Skeleton {...skeletonColors} height={40} width={40} circle={true} />
                        </div>
                        <div className="col-span-4">
                            <Skeleton {...skeletonColors} height={15} width={100} />
                            <Skeleton {...skeletonColors} height={15} width={150} />
                        </div>
                        <div className="col-span-6 flex justify-end gap-2 items-center">
                            <Skeleton {...skeletonColors} height={30} width={10} />
                        </div>
                        <div className="col-span-12 w-full flex justify-start gap-2 items-center">
                            <Skeleton {...skeletonColors} height={150} width={320} />
                        </div>
                    </div>
                )}

                {!NeighborhoodNews.loading && (
                    <div className="px-2 pb-1 my-2 border-b border-gray-200 dark:border-gray-800/50">
                        <div className="flex items-start">
                            <div className="w-14">
                                <img
                                    onClick={() => handleNavigate(newsDetails?.created_by?.id)}
                                    alt={newsDetails?.created_by?.firstName}
                                    src={newsDetails?.created_by?.avatar}
                                    className="w-10 h-10 object-cover rounded-full cursor-pointer"
                                />
                            </div>
                            <div className="flex items-start justify-between w-full ml-1">
                                <div className="flex flex-col">
                                    <p
                                        className="text-[14px] font-bold cursor-pointer"
                                        onClick={() => handleNavigate(newsDetails?.created_by?.id)}
                                    >
                                        {newsDetails?.created_by?.full_name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                        {formatTimeOnly(newsDetails?.created_at)}
                                    </p>
                                    <p className="flex items-center text-[10px] text-gray-500">
                                        <IoLocationOutline className="text-[14px] ml-[-3px]" />
                                        {newsDetails?.news_location?.city},{" "}
                                        {newsDetails?.news_location?.state}
                                    </p>
                                </div>
                                <div className="relative" ref={menuRef}>
                                    <HiOutlineDotsVertical
                                        className="text-[18px] cursor-pointer text-gray-600 mr-[-5px]"
                                        onClick={(event) =>
                                            handleMenuToggle(newsDetails?.id, event)
                                        }
                                    />
                                    {activeMenuId === newsDetails?.id && (
                                        <div className="absolute right-0 mt-2 w-[4.5rem] bg-white dark:bg-[#242323] border-2 border-[#BDE6FF] overflow-hidden rounded shadow-lg">
                                            {User_profile?.id === newsDetails?.created_by?.id && (
                                                <>
                                                    <div
                                                        onClick={() => handleDeleteNeighborhoodNews(newsDetails?.id)}
                                                        className="px-2 py-1 text-[12px] border-b border-gray-200 dark:border-gray-800/50 text-gray-700 dark:text-gray-100 dark:hover:bg-[#444444] hover:bg-gray-100 hover:text-black cursor-pointer"
                                                    >
                                                        Delete
                                                    </div>
                                                    <div
                                                        onClick={() => navigate(`/neighborhood/news/update/${newsDetails?.id}`)}
                                                        className="px-2 py-1 text-[12px] text-gray-700 dark:text-gray-100 dark:hover:bg-[#444444] hover:bg-gray-100 hover:text-black cursor-pointer"
                                                    >
                                                        Edit
                                                    </div>
                                                </>
                                            )}
                                            {User_profile?.id !== newsDetails?.created_by?.id && (
                                                <>
                                                    <div
                                                        onClick={() => handleStartChat(newsDetails?.created_by?.id)}
                                                        className="px-2 py-1 text-[12px] border-b border-gray-200 dark:border-gray-800/50 text-gray-700 dark:text-gray-100 dark:hover:bg-[#444444] hover:bg-gray-100 hover:text-black cursor-pointer"
                                                    >
                                                        Message
                                                    </div>
                                                    <div
                                                        onClick={() => setIsReportNews(!isReportNews)}
                                                        className="px-2 py-1 text-[12px] border-b border-gray-200 dark:border-gray-800/50 text-gray-700 dark:text-gray-100 dark:hover:bg-[#444444] hover:bg-gray-100 hover:text-black cursor-pointer"
                                                    >
                                                        Report
                                                    </div>
                                                    <div
                                                        onClick={handleBlockUser}
                                                        className="px-2 py-1 text-[12px] text-gray-700 dark:text-gray-100 dark:hover:bg-[#444444] hover:bg-gray-100 hover:text-black cursor-pointer"
                                                    >
                                                        Block
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-2">
                            <p className="text-[12px] font-bold">{newsDetails?.title}</p>
                            <img
                                alt={newsDetails?.title}
                                src={newsDetails?.post_image}
                                className="w-full h-[160px] object-cover rounded-lg mt-1 cursor-pointer"
                            />
                            <p className="text-[10px] leading-[15px] mt-2 cursor-pointer">
                                {newsDetails?.news_description &&
                                    newsDetails.news_description.split(" ")?.length > 40 &&
                                    !expandedNews[newsDetails.id] ? (
                                    <>
                                        {newsDetails.news_description
                                            .split(" ")
                                            .slice(0, 20)
                                            .join(" ")}
                                        ...
                                        <span
                                            className="text-blue-500 ml-1"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleExpand(newsDetails.id);
                                            }}
                                        >
                                            See more
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        {newsDetails?.news_description}
                                        {newsDetails?.news_description &&
                                            newsDetails.news_description.split(" ")?.length > 100 && (
                                                <span
                                                    className="text-blue-500 ml-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleExpand(newsDetails.id);
                                                    }}
                                                >
                                                    Show less
                                                </span>
                                            )}
                                    </>
                                )}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                                <div className="flex items-center gap-x-4 relative">
                                    <div className="flex flex-col items-center">
                                        {newsDetails?.is_liked ? (
                                            <FaHeart
                                                className="text-[16px] text-red-600 cursor-pointer"
                                                onClick={() =>
                                                    handleLikeNeighborhoodNews(newsDetails?.id)
                                                }
                                            />
                                        ) : (
                                            <FaRegHeart
                                                className="text-[16px] text-gray-600 cursor-pointer"
                                                onClick={() =>
                                                    handleLikeNeighborhoodNews(newsDetails?.id)
                                                }
                                            />
                                        )}
                                        <p className="text-[8px] text-gray-500">
                                            {newsDetails?.likes_count}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <FaRegComment
                                            className="text-[16px] text-gray-600 cursor-pointer"
                                            onClick={() => newsDetails?.allow_responses ? setCommentPopupOpen(true) : null}
                                        />
                                        <p className="text-[8px] text-gray-500">
                                            {newsDetails?.comments_count}
                                        </p>
                                    </div>
                                    {sharePopupOpen && (
                                        <div
                                            ref={sharePopupRef}
                                            className="absolute left-24 -top-10 p-1 w-[10rem] bg-white dark:bg-[#333333] border dark:border-gray-800/50 overflow-hidden rounded shadow-lg"
                                        >
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="search"
                                                    value={searchInput}
                                                    onChange={(e) => setSearchInput(e.target.value)}
                                                    className="w-full h-6 pl-2 pr-6 py-[2px] border dark:bg-[#272626] border-gray-800 placeholder:text-[12px] text-[10px] rounded-lg focus:outline-none"
                                                />
                                                <CiSearch className="absolute right-1 top-1/2 transform -translate-y-1/2 transition-transform text-gray-500 hover:text-gray-700 text-[18px] cursor-pointer" />
                                            </div>
                                            <div className="flex items-center justify-between p-1">
                                                <div className="flex items-center gap-x-3 overflow-x-scroll">
                                                    {filteredResults?.length > 0 ? (
                                                        filteredResults.map((data) => (
                                                            <div
                                                                className="flex flex-col items-center cursor-pointer"
                                                                key={data.id}
                                                            >
                                                                <div className="h-6 w-6">
                                                                    <img
                                                                        alt={data.firstName}
                                                                        src={data.profileImage}
                                                                        className="w-6 h-6 object-cover rounded-full"
                                                                    />
                                                                </div>
                                                                <p className="text-[8px]">{data.firstName}</p>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <p className="text-[8px] text-gray-500">
                                                            No results found
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {newsDetails?.is_saved ? (
                                    <BsBookmarkCheckFill
                                        className="text-[18px] text-gray-600 dark:text-blue-400 mr-[-2px] cursor-pointer"
                                        onClick={() => handleNewsSave(newsDetails.id)}
                                    />
                                ) : (
                                    <CiBookmarkPlus
                                        className="text-[22px] text-gray-600 mr-[-5px] cursor-pointer"
                                        onClick={() => handleNewsSave(newsDetails.id)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="px-2 mt-2 h-[15vh] flex items-center justify-center">
                {!newsDetails?.allow_responses && (
                    <p className="text-[10px] text-gray-500 mt-1">
                        Comments are not allowed for this news.
                    </p>
                )}
            </div>

            {/* Comment Popup Modal */}
            <AnimatePresence>
                {commentPopupOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="comment-modal-overlay absolute inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
                        onClick={(e) => {
                            if (e.target.classList.contains('comment-modal-overlay')) {
                                setCommentPopupOpen(false);
                                setSelectedCommentId(null);
                                setNewsComment("");
                            }
                        }}
                    >
                        <motion.div
                            ref={commentPopupRef}
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="bg-white dark:bg-[#333333] rounded-t-2xl shadow-lg w-full max-w-full h-[70%] flex flex-col"
                        >
                            <div className="flex flex-col items-center pt-2 pb-3 border-b border-gray-200 dark:border-gray-800/50">
                                <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mb-3"></div>
                                <div className="flex items-center justify-center w-full px-4">
                                    <p className="text-[16px] font-semibold dark:text-gray-100">
                                        Comments ({newsDetails?.comments_count || 0})
                                    </p>
                                </div>
                            </div>

                            <div
                                className="flex-1 overflow-y-auto"
                                onScroll={handleCommentsScroll}
                            >

                                <Comment id={id}
                                    comments={comments}
                                    setComments={setComments}
                                    reportComment={handleReportComment}
                                    loading={neighborhoodNewsComment.loading}
                                    handleEditNewsComment={(commentId) => {
                                        setSelectedCommentId(commentId);
                                        const commentToEdit = comments.find((comment) => comment.id === commentId);
                                        if (commentToEdit) {
                                            setNewsComment(commentToEdit.body);
                                        }
                                    }}
                                    handleDeleteNewsComment={handleDeleteNewsComment}
                                    handleLikefn={handleLikeNeighborhoodNewsComment}
                                />

                                {loadingMoreComments && (
                                    <div className="flex justify-center items-center py-4">
                                        <BeatLoader size={8} color="#666" />
                                        <span className="ml-2 text-sm text-gray-500">Loading more comments...</span>
                                    </div>
                                )}
                            </div>

                            <div className="p-2 border-t border-gray-200 dark:border-gray-800/50 bg-white dark:bg-[#333333]">
                                <div className="w-full relative">
                                    <FaRegComment className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 mr-3 text-[17px]" />
                                    <input
                                        type="text"
                                        value={newsComment}
                                        onChange={(e) => setNewsComment(e.target.value)}
                                        placeholder={selectedCommentId ? "Update your comment..." : "Add a comment..."}
                                        className="w-full bg-transparent text-[14px] dark:text-gray-200 placeholder-gray-400 outline-none border-2 border-[#BDE6FF] rounded-lg px-10 py-1.5"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newsComment.trim()) {
                                                selectedCommentId
                                                    ? submitEditNewsComment(selectedCommentId)
                                                    : handleAddCommentNews();
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={() =>
                                            selectedCommentId
                                                ? submitEditNewsComment(selectedCommentId)
                                                : handleAddCommentNews()
                                        }
                                        disabled={!newsComment.trim() || neighborhoodNewsComment.loading}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors duration-300"
                                    >
                                        {neighborhoodNewsComment.loading ? (
                                            <BeatLoader
                                                size={6}
                                                color={"#666"}
                                                loading={neighborhoodNewsComment.loading}
                                            />
                                        ) : (
                                            <SendHorizontal className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>

                                {selectedCommentId && (
                                    <button
                                        onClick={() => {
                                            setSelectedCommentId(null);
                                            setNewsComment("");
                                        }}
                                        className="mt-3 text-[12px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                    >
                                        Cancel editing
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {commentResport && (
                <div className="absolute h-[82vh] inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-2 dark:bg-[#1E1D21] shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center">
                            Report Comment
                        </p>
                        <p className="text-[12px] text-black mb-2 dark:text-white text-center">
                            Are you sure you want to report this comment?
                        </p>
                        <div className="px-2 w-full">
                            <label htmlFor="comment-report-reason" className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <div className="relative custom-dropdown">
                                <div
                                    onClick={() => setShowCommentReasonDropdown(!showCommentReasonDropdown)}
                                    className="w-full h-[35px] border border-gray-400 dark:border-gray-600 bg-white dark:bg-[#1E1D21] rounded-lg p-1 text-[12px] focus:outline-none cursor-pointer flex items-center justify-between"
                                >
                                    <span className={commentReportReason ? "text-black dark:text-white" : "text-gray-500"}>
                                        {commentReportReason || "Select a reason"}
                                    </span>
                                    <IoIosArrowDown className="text-gray-500" />
                                </div>
                                {showCommentReasonDropdown && (
                                    <div className="absolute top-[36px] left-0 w-full bg-white dark:bg-[#1E1D21] border border-gray-400 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-[125px] overflow-y-auto">
                                        {reportReasons.map((reasonOption, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleCommentReasonSelect(reasonOption)}
                                                className="px-2 py-1.5 text-[12px] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-200 dark:border-gray-800/50 last:border-b-0"
                                            >
                                                {reasonOption}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="px-2 w-full mt-2">
                            <label htmlFor="comment-report-details" className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                                Optional Details
                            </label>
                            <textarea
                                rows={6}
                                value={reportCommentText}
                                maxLength={200}
                                id="comment-report-details"
                                placeholder="Type your report here..."
                                onChange={(e) => setReportCommentText(e.target.value)}
                                className="w-full h-[50px] border border-gray-400 dark:border-gray-600 dark:bg-[#1E1D21] rounded-lg p-1.5 text-[12px] focus:outline-none resize-none"
                            />
                        </div>
                        <div className="flex gap-2 my-2">
                            <Button
                                onClick={() => {
                                    setCommentResport(false);
                                    setReportCommentText("");
                                    setCommentReportReason("");
                                    setSelectedCommentId(null);
                                }}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={submitReportComment}
                                size="sm"
                                className="border border-gray-400 dark:border-gray-800/50 bg-red-900 hover:bg-red-600 text-white"
                            >
                                {reportNews.loading ? <BeatLoader size={10} color={"#fff"} loading={reportNews.loading} /> : " Report"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {isReportNews && (
                <div className="absolute h-[82vh] inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-2 dark:bg-[#1E1D21] shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center">
                            Report News
                        </p>
                        <p className="text-[12px] text-black mb-2 dark:text-white text-center">
                            Are you sure you want to report this news?
                        </p>
                        <div className="px-2 w-full">
                            <label htmlFor="report-reason" className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <div className="relative custom-dropdown">
                                <div
                                    onClick={() => setShowReasonDropdown(!showReasonDropdown)}
                                    className="w-full h-[35px] border border-gray-400 dark:border-gray-600 bg-white dark:bg-[#1E1D21] rounded-lg p-1 text-[12px] focus:outline-none cursor-pointer flex items-center justify-between"
                                >
                                    <span className={reportReason ? "text-black dark:text-white" : "text-gray-500"}>
                                        {reportReason || "Select a reason"}
                                    </span>
                                    <IoIosArrowDown className="text-gray-500" />
                                </div>
                                {showReasonDropdown && (
                                    <div className="absolute top-[36px] left-0 w-full bg-white dark:bg-[#1E1D21] border border-gray-400 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-[125px] overflow-y-auto">
                                        {reportReasons.map((reasonOption, index) => (
                                            <div
                                                key={index}
                                                onClick={() => handleReasonSelect(reasonOption)}
                                                className="px-2 py-1.5 text-[12px] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-200 dark:border-gray-800/50 last:border-b-0"
                                            >
                                                {reasonOption}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="px-2 w-full mt-2">
                            <label htmlFor="report-details" className="block text-[12px] mb-0.5 font-semibold text-black dark:text-white">
                                Optional Details
                            </label>
                            <textarea
                                rows={6}
                                value={newsReportText}
                                maxLength={200}
                                id="report-details"
                                placeholder="Type your report here..."
                                onChange={(e) => setNewsReportText(e.target.value)}
                                className="w-full h-[50px] border border-gray-400 dark:border-gray-600 dark:bg-[#1E1D21] rounded-lg p-1.5 text-[12px] focus:outline-none resize-none"
                            />
                        </div>
                        <div className="flex gap-2 my-2">
                            <Button
                                onClick={() => {
                                    setIsReportNews(false);
                                    setNewsReportText("");
                                    setReportReason("");
                                }}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={submitReportNews}
                                size="sm"
                                className="border border-gray-400 dark:border-gray-800/50 bg-red-900 hover:bg-red-600 text-white"
                            >
                                {reportNews.loading ? <BeatLoader size={10} color={"#fff"} loading={reportNews.loading} /> : " Report"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Block User Confirmation Popup */}
            {showBlockConfirm && (
                <div className="absolute h-[82vh] inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white h-auto py-4 dark:bg-[#1E1D21] shadow-lg rounded-lg w-[300px] flex flex-col items-center justify-center">
                        <p className="text-[24px] font-bold text-black dark:text-white text-center mb-2">
                            Block {blockUserInfo?.full_name || "User"}?
                        </p>
                        <p className="text-[12px] text-black px-4 dark:text-white text-center">
                            Are you sure you want to block {blockUserInfo?.full_name || "this user"}? They will not be able to send you messages,
                            find your profile, or see your posts and they will not be notified that you blocked them.
                        </p>
                        <div className="flex gap-2 mt-4">
                            <Button
                                onClick={handleBlockCancel}
                                size="sm"
                                className="bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleBlockConfirm}
                                size="sm"
                                className="bg-red-900 hover:bg-red-600 text-white"
                            >
                                Block
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default NewsDetails;
